import { MANAGED_SERVICES } from '@gjl/voice/pricing'

/**
 * Single source for the site's primary navigation. Header (desktop dropdowns)
 * and MobileNav (grouped drawer) both render from this, so they cannot drift —
 * and since this now lives in the shared chrome, neither can the two Vite
 * builds that serve fuguelab.com.
 */

export interface NavLink {
  label: string
  href: string
  description?: string
}

/** A titled column within a mega-menu group. */
export interface NavColumn {
  heading: string
  items: NavLink[]
}

export interface NavGroup {
  label: string
  /** Flat list of every link in the group. MobileNav and active-state
   *  detection read this; for mega-menu groups it is derived from `columns`
   *  so the two never drift. */
  items: NavLink[]
  /** When present, the desktop header renders this group as a multi-column
   *  mega menu instead of a single dropdown. */
  columns?: NavColumn[]
  footer?: { label: string; href: string }
}

/**
 * Identity (slug + display name) of the two page families the nav enumerates.
 *
 * Only the identity lives here. The PROSE for these pages — headlines, proof,
 * feature tables, ~1,000 lines of it — stays in `studio/client/src/data/`,
 * because the hymnal renders none of it and object literals do not tree-shake
 * per-property: importing the full records here would ship every word of
 * studio's comparison copy inside all 968 prerendered hymnal pages.
 *
 * `studio/client/src/data/navigation-identity.test.ts` asserts these lists
 * agree with the full records, so a page added on one side cannot silently go
 * missing from the nav on the other.
 */
export const AUDIENCE_SEGMENTS: readonly { slug: string; name: string }[] = [
  { slug: 'music-festivals', name: 'Music Festivals' },
  { slug: 'choral-festivals', name: 'Choral Festivals & Choruses' },
  { slug: 'young-artist-programs', name: 'Young Artist & Conducting Programs' },
  { slug: 'pre-college-summer-programs', name: 'Pre-College & Summer Programs' },
  { slug: 'churches-sacred-music', name: 'Churches & Sacred Music' },
  { slug: 'music-schools', name: 'Music Schools & Studios' },
]

export const COMPETITORS: readonly { slug: string; name: string }[] = [
  { slug: 'acceptd', name: 'Acceptd' },
  { slug: 'submittable', name: 'Submittable' },
  { slug: 'yaptracker', name: 'YAP Tracker' },
  { slug: 'squarespace', name: 'Squarespace' },
  { slug: 'wix', name: 'Wix' },
  { slug: 'wordpress', name: 'WordPress' },
  { slug: 'eventbrite', name: 'Eventbrite' },
]

/** Solutions is a mega menu: capabilities grouped by the job they do. */
export const solutionsColumns: NavColumn[] = [
  {
    heading: 'Websites & Applications',
    items: [
      {
        label: 'Websites for Music Organizations',
        href: '/services/websites',
        description: 'Designed for your organization, built and run for you',
      },
      {
        label: 'Applications & Auditions',
        href: '/services/applications',
        description: 'Music-native applications and auditions with payments and review',
      },
    ],
  },
  {
    heading: 'Audience & Revenue',
    items: [
      {
        label: 'Email Marketing',
        href: '/services/email-marketing',
        description: 'Newsletters and season campaigns, one audience record',
      },
      {
        label: 'Donor Management',
        href: '/services/donor-management',
        description: 'Free to accept (0% platform fee); recurring giving, receipts, donor records',
      },
      {
        label: 'Ticketing',
        href: '/services/ticketing',
        description: 'Sell on your own site: 1.5% per ticket, QR check-in',
      },
    ],
  },
  {
    heading: 'Music & Creative',
    items: [
      {
        label: 'Music Library & Rehearsal Manager',
        href: '/music-manager',
        description: 'Music library, rehearsal planning, ensembles (Canticle for churches)',
      },
      {
        label: 'Graphic Design',
        href: '/services/design',
        description: 'Brand identity, posters, programs, season campaigns',
      },
      {
        label: 'Photography',
        href: '/services/photography',
        description: 'Concert coverage, headshots, season photography days',
      },
    ],
  },
  {
    heading: 'Ongoing & Platform',
    items: [
      {
        label: 'Ongoing Partnership',
        href: '/services/digital-partnership',
        description: 'We keep running it with you after launch',
      },
      { label: 'The App', href: '/platform', description: 'The self-serve app your site runs on (fuguelab.app)' },
    ],
  },
]

export const solutionsGroup: NavGroup = {
  label: 'Solutions',
  items: solutionsColumns.flatMap((column) => column.items),
  columns: solutionsColumns,
  footer: { label: 'All services', href: '/services' },
}

export const doneForYouGroup: NavGroup = {
  label: 'Done For You',
  items: [
    // Website Care is a tier of Managed Website, not its own service, so it is
    // folded into the single Managed Website entry (see MANAGED_WEBSITE).
    ...MANAGED_SERVICES.filter((svc) => svc.id !== 'website-care').map((svc) => ({
      label: svc.name,
      href: `/services/done-for-you#${svc.id}`,
    })),
    {
      label: 'Concierge',
      href: '/services/done-for-you#concierge',
      description: 'Everything done for you, plus strategy',
    },
    {
      label: 'Consulting',
      href: '/pricing#consulting',
      description: 'Strategy intensives and fractional digital direction',
    },
  ],
  footer: { label: 'All done-for-you services', href: '/services/done-for-you' },
}

export const whoWeServeGroup: NavGroup = {
  label: 'Who We Serve',
  items: AUDIENCE_SEGMENTS.map((segment) => ({
    label: segment.name,
    href: `/who-we-help/${segment.slug}`,
  })),
  footer: { label: 'Everyone we help', href: '/who-we-help' },
}

export const compareGroup: NavGroup = {
  label: 'Compare',
  items: COMPETITORS.map((competitor) => ({
    label: `vs ${competitor.name}`,
    href: `/compare/${competitor.slug}`,
  })),
  footer: { label: 'All comparisons', href: '/compare' },
}

export const resourcesGroup: NavGroup = {
  label: 'Resources',
  items: [
    { label: 'Integrations', href: '/integrations' },
    { label: 'Portfolio', href: '/portfolio' },
    { label: 'Blog & Case Studies', href: '/blog' },
    { label: 'Resource Library', href: '/resources' },
    // A THIRD Vite build on this origin (projects/tooling/design-lab), served
    // from /design/. Studio's Link adapter is react-router's, so this entry
    // crosses a bundle boundary the router cannot follow — the chrome renders
    // dropdown items through ChromeLink, and studio's adapter would try to
    // client-route it. Keep this as the only reason to check that adapter if
    // the link ever 404s without a network request.
    // THIRD LABEL, AND THE SAME ARGUMENT WON EACH TIME — buyer language.
    // "Design System" was first and is jargon: a festival director does not shop
    // for a design system. "Design Options" (#3480) fixed the jargon but stayed a
    // noun phrase naming OUR inventory. "Design your site" is what the visitor is
    // there to do, and it matches the page's own title — "See what your site could
    // look like" — which was already second-person while the nav entry was not.
    // Do not revert it to a noun.
    { label: 'Design your site', href: '/design/' },
    { label: 'About', href: '/about' },
  ],
}

export const navGroups: NavGroup[] = [solutionsGroup, doneForYouGroup, whoWeServeGroup, compareGroup, resourcesGroup]

/** Top-level links that are not dropdowns. */
export const pricingLink: NavLink = { label: 'Pricing', href: '/pricing' }
